"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import {
  ApiError,
  api,
  type PostVisibility,
  type TopicMarketWithUserPosition,
  type TopicRecord,
} from "@/lib/api";
import { useKycGateRedirect } from "@/lib/useKycGateRedirect";
import { TenorGifPicker } from "./TenorGifPicker";
import { QuotedPostEmbed } from "./posts/QuotedPostEmbed";
import {
  MentionAutocompleteList,
  useMentionAutocomplete,
} from "./social/MentionAutocomplete";
import type { QuotedPost } from "@/lib/api";

/**
 * EPIC-047 — Unified Post Composer modal.
 *
 * One component used everywhere the user authors a post or topic
 * discussion: SiteHeader "+ Post", topic page "+ New discussion",
 * profile "+ Post", trade-confirm "Add commentary".
 *
 * Composer surface (founder spec 2026-05-01, mirrors StockTwits +
 * Wild Apricot create-post):
 *  - Avatar + body textarea
 *  - Bullish / Bearish sentiment chips
 *  - Toolbar: Image / GIF / File / Emoji (stubbed; backend follows
 *    once STORY-2101 unblocks object storage)
 *  - Destination dropdown (My profile / Anonymous / A topic)
 *  - When "A topic" picked: topic search picker (CmdK-style) +
 *    optional "Attach a position" market picker rooted in the user's
 *    open positions on the topic's markets
 *  - Visibility selector (5 values: Public / All Members / Followers /
 *    Subscribers / Only Me)
 *  - Publish (right-aligned, disabled until valid)
 *
 * Submission routes:
 *  - Destination = "My profile" (or "Anonymous"): POST /v1/posts.
 *  - Destination = a topic: POST /v1/topics/:slug/discussions with
 *    optional `attachedMarketId` from the position picker.
 */

export type PostComposerDestination =
  | { kind: "profile"; anonymous?: boolean }
  | { kind: "topic"; slug: string; name: string };

export interface PostComposerProps {
  open: boolean;
  onClose: () => void;
  // Pre-populate destination (e.g., topic page passes the current
  // topic; trade-confirm modal passes the market's primary topic +
  // pre-attaches the market). Defaults to "My profile" when omitted.
  initialDestination?: PostComposerDestination;
  // Pre-attach a market id (used by the trade-confirm "Add commentary"
  // entry). Only applies when destination is a topic.
  initialAttachedMarketId?: string | null;
  // Optional pre-filled body (e.g., trade-confirm "I just bought 250
  // shares of YES on $BTC-200K").
  initialBody?: string;
  // Optional sentiment hint from the trade-confirm flow.
  initialSentiment?: "bullish" | "bearish" | null;
  // 2026-05-05 — Twitter-style quote-tweet popup. When set, the modal
  // renders the QuotedPostEmbed at the top + submits with
  // quotedPostId so the resulting post embeds the original.
  // Founder direction: clicking Quote on a feed card should open a
  // composer modal (Twitter pattern) instead of navigating to
  // /posts/new?quote=<id>.
  initialQuotedPost?: QuotedPost | null;
  // Fired after a successful publish so the host page can refresh.
  onPublished?: (result: { kind: "post" | "discussion"; id: string }) => void;
}

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: PostVisibility;
  label: string;
  helper: string;
}> = [
  { value: "public", label: "Public", helper: "Visible to anyone" },
  {
    value: "authed",
    label: "All Members",
    helper: "Signed-in users only — hidden from the public internet",
  },
  {
    value: "followers",
    label: "Followers",
    helper: "Only people who follow you",
  },
  {
    value: "subscribers",
    label: "Subscribers",
    helper: "Paid subscribers only",
  },
  {
    value: "private",
    label: "Only Me",
    helper: "Save as a private draft — nobody else sees it",
  },
];

export function PostComposer(props: PostComposerProps) {
  const router = useRouter();
  const kycRedirect = useKycGateRedirect();
  const {
    open,
    onClose,
    initialDestination,
    initialAttachedMarketId,
    initialBody,
    initialSentiment,
    initialQuotedPost,
    onPublished,
  } = props;

  const [body, setBody] = useState(initialBody ?? "");
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const mention = useMentionAutocomplete(bodyRef, body, setBody);
  const [title, setTitle] = useState("");
  const [sentiment, setSentiment] = useState<"bullish" | "bearish" | null>(
    initialSentiment ?? null,
  );
  const [visibility, setVisibility] = useState<PostVisibility>("public");
  const [authorAnonymous, setAuthorAnonymous] = useState(
    initialDestination?.kind === "profile" &&
      initialDestination.anonymous === true,
  );
  const [destination, setDestination] = useState<PostComposerDestination>(
    initialDestination ?? { kind: "profile" },
  );
  const [attachedMarketId, setAttachedMarketId] = useState<string | null>(
    initialAttachedMarketId ?? null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [destinationOpen, setDestinationOpen] = useState(false);
  // EPIC-041 STORY-2102 — selected Tenor GIF URLs (also reused for any
  // future paste-URL photo flow). Backend stores them in `posts.photos`
  // alongside future drag-uploaded images. The composer caps at the
  // server's `feed.photo_max_per_post` (default 4); we mirror that
  // ceiling client-side as a defensive UX.
  const [photos, setPhotos] = useState<string[]>([]);
  const [gifPickerOpen, setGifPickerOpen] = useState(false);

  // Reset on open so re-opening doesn't carry stale state.
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBody(initialBody ?? "");
    setTitle("");
    setSentiment(initialSentiment ?? null);
    setVisibility("public");
    setAuthorAnonymous(
      initialDestination?.kind === "profile" &&
        initialDestination.anonymous === true,
    );
    setDestination(initialDestination ?? { kind: "profile" });
    setAttachedMarketId(initialAttachedMarketId ?? null);
    setPhotos([]);
    setGifPickerOpen(false);
    setError(null);
  }, [open, initialBody, initialSentiment, initialDestination, initialAttachedMarketId]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const canPublish = useMemo(() => {
    if (submitting) return false;
    if (body.trim().length === 0) return false;
    return true;
  }, [submitting, body]);

  async function handlePublish() {
    if (!canPublish) return;
    setSubmitting(true);
    setError(null);
    try {
      if (destination.kind === "topic") {
        const res = await api.createTopicDiscussion(destination.slug, {
          title: title.trim().length > 0 ? title.trim() : body.trim().slice(0, 80),
          body: body.trim(),
          attachedMarketId,
        });
        onPublished?.({ kind: "discussion", id: res.discussion.id });
        // Route is /topic/[slug]/d/[discussionId] — there's no
        // standalone /discussions/[id] page, so the old redirect
        // landed on Next's 404.
        router.push(
          `/topic/${encodeURIComponent(destination.slug)}/d/${res.discussion.id}`,
        );
        onClose();
      } else {
        const res = await api.createPost({
          title: title.trim().length > 0 ? title.trim() : null,
          bodyMd: body.trim(),
          visibility,
          authorAnonymous,
          sentiment: sentiment ?? undefined,
          photos: photos.length > 0 ? photos : undefined,
          quotedPostId: initialQuotedPost?.id ?? null,
        });
        onPublished?.({ kind: "post", id: res.id });
        router.push(`/posts/${res.id}`);
        onClose();
      }
    } catch (err) {
      // EPIC-050 STORY-2200 — opens the Kyc1AutoModal on a Tier-1
      // shortfall instead of leaving the user with a raw "Identity
      // details required" inline message.
      if (kycRedirect(err)) {
        setSubmitting(false);
        return;
      }
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to publish.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;
  // SSR safety — document only exists on the client. The composer is
  // a "use client" component but during initial hydration the
  // server-rendered HTML doesn't have a portal target; bail out and
  // let the next render attach.
  if (typeof document === "undefined") return null;

  // SiteHeader uses backdrop-blur-md which CSS-spec-mandates creates
  // a containing block for `position: fixed` descendants. Without a
  // Portal the modal anchors to the SiteHeader's box (right-aligned
  // off-canvas). createPortal escapes to <body> so `fixed inset-0`
  // is once again viewport-relative.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 backdrop-blur-sm sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="New post"
    >
      {/* Mobile: full-screen sheet (no rounding, no margin, fills the
          viewport). Desktop (sm+): centered card with mx-4 + rounded
          corners. Inner column is scrollable so the modal never
          clips on short viewports / phone landscape. */}
      <div className="flex w-full max-w-xl flex-col bg-[var(--color-surface)] shadow-2xl sm:mx-4 sm:my-4 sm:max-h-[calc(100dvh-2rem)] sm:rounded-lg sm:border sm:border-[var(--color-border)]">
        <header className="flex flex-shrink-0 items-center justify-between px-4 py-3 border-b border-[var(--color-border)] sm:px-5">
          <h2 className="text-base font-semibold text-[var(--color-text)]">
            New post
          </h2>
          <button
            type="button"
            className="text-[var(--color-text3)] hover:text-[var(--color-text)] text-2xl leading-none p-1 -mr-1"
            onClick={onClose}
            aria-label="Close composer"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 sm:p-5">
          <input
            type="text"
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-transparent border-b border-[var(--color-border)] px-1 py-2 text-[var(--color-text)] placeholder:text-[var(--color-text3)] focus:outline-none focus:border-[var(--color-border2)]"
            maxLength={280}
          />

          <div className="relative">
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={mention.onKeyDown}
              onBlur={() => {
                // Slight delay so the mention list's onMouseDown can
                // fire before close() wipes state.
                setTimeout(() => mention.close(), 120);
              }}
              placeholder={
                initialQuotedPost ? "Add a comment…" : "Post"
              }
              rows={6}
              className="w-full resize-none bg-transparent px-1 py-1 text-[var(--color-text)] placeholder:text-[var(--color-text3)] focus:outline-none"
              maxLength={20000}
              autoFocus
            />
            <MentionAutocompleteList handle={mention} />
          </div>

          {/* 2026-05-05 — Twitter-style quote-tweet popup. When the
              composer was opened with a quoted post, render the
              QuotedPostEmbed below the textarea so the user sees
              what they're quoting. Founder direction 2026-05-05:
              "when the user is quoting someone pop up. like
              twitter (clone)". */}
          {initialQuotedPost && (
            <div className="mt-2">
              <QuotedPostEmbed quoted={initialQuotedPost} />
            </div>
          )}

          {/* EPIC-047 — Bullish / Bearish sentiment chips */}
          <div className="flex items-center gap-2">
            <SentimentChip
              tone="bullish"
              active={sentiment === "bullish"}
              onClick={() =>
                setSentiment(sentiment === "bullish" ? null : "bullish")
              }
            />
            <SentimentChip
              tone="bearish"
              active={sentiment === "bearish"}
              onClick={() =>
                setSentiment(sentiment === "bearish" ? null : "bearish")
              }
            />
            <span className="text-xs text-[var(--color-text3)] ml-2">
              {sentiment ? "Tagged" : "Optional sentiment tag"}
            </span>
          </div>

          {/* EPIC-047 — composer toolbar. Drag-drop photo zone +
              EPIC-041 STORY-2102 GIF button. Photo upload backend
              stubbed until STORY-2101 unblocks object storage; GIF
              picker activates when NEXT_PUBLIC_TENOR_API_KEY is set. */}
          <ComposerToolbar
            photos={photos}
            onRemovePhoto={(idx) =>
              setPhotos((prev) => prev.filter((_, i) => i !== idx))
            }
            onOpenGifPicker={() => setGifPickerOpen(true)}
            gifDisabled={photos.length >= 4}
          />

          {/* EPIC-047 — destination picker */}
          <DestinationPickerRow
            destination={destination}
            authorAnonymous={authorAnonymous}
            open={destinationOpen}
            onToggle={() => setDestinationOpen((v) => !v)}
            onClose={() => setDestinationOpen(false)}
            onChange={(next) => {
              setDestination(next);
              if (next.kind !== "profile") {
                setAuthorAnonymous(false);
              }
              if (next.kind === "topic") {
                setVisibility("public");
              }
              setAttachedMarketId(null);
              setDestinationOpen(false);
            }}
            onAnonymousToggle={(v) => setAuthorAnonymous(v)}
          />

          {/* EPIC-047 — when destination is a topic, surface attach-position picker */}
          {destination.kind === "topic" ? (
            <MarketPositionAttacher
              topicSlug={destination.slug}
              attachedMarketId={attachedMarketId}
              onChange={(id) => setAttachedMarketId(id)}
            />
          ) : (
            <VisibilitySelector
              value={visibility}
              onChange={(v) => setVisibility(v)}
            />
          )}

          {error ? (
            <div
              className="rounded px-3 py-2 text-sm"
              style={{
                borderWidth: 1,
                borderStyle: "solid",
                borderColor:
                  "color-mix(in srgb, var(--color-no) 40%, transparent)",
                background:
                  "color-mix(in srgb, var(--color-no) 10%, transparent)",
                color: "var(--color-status-negative)",
              }}
            >
              {error}
            </div>
          ) : null}
        </div>

        {/* EPIC-041 STORY-2102 — Tenor GIF picker modal. Renders above
            the composer's modal layer (z-60 vs z-50) so the user can
            interact with both stacked. */}
        <TenorGifPicker
          open={gifPickerOpen}
          onClose={() => setGifPickerOpen(false)}
          onPick={(url) =>
            setPhotos((prev) =>
              prev.length >= 4 ? prev : [...prev, url],
            )
          }
        />

        <footer className="flex flex-shrink-0 items-center justify-between gap-3 px-4 py-3 border-t border-[var(--color-border)] sm:px-5">
          <span className="text-xs text-[var(--color-text3)]">
            {body.trim().length} / 20000
          </span>
          <button
            type="button"
            onClick={handlePublish}
            disabled={!canPublish}
            className="rounded-md px-4 py-1.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
            style={{ background: "var(--color-brand)" }}
          >
            {submitting ? "Publishing…" : "Publish"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function SentimentChip(props: {
  tone: "bullish" | "bearish";
  active: boolean;
  onClick: () => void;
}) {
  const tone = props.tone;
  const colorVar =
    tone === "bullish" ? "var(--color-yes)" : "var(--color-no)";
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="px-3 py-1 rounded-full text-xs font-medium border transition-colors"
      style={
        props.active
          ? {
              background: colorVar,
              color: "#fff",
              borderColor: colorVar,
            }
          : {
              background: "transparent",
              color: colorVar,
              borderColor: `color-mix(in srgb, ${colorVar} 40%, transparent)`,
            }
      }
      aria-pressed={props.active}
    >
      {tone === "bullish" ? "↗ Bullish" : "↘ Bearish"}
    </button>
  );
}

function ComposerToolbar(props: {
  photos: string[];
  onRemovePhoto: (idx: number) => void;
  onOpenGifPicker: () => void;
  gifDisabled: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled
          title="Image upload coming soon"
          className="rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text3)] cursor-not-allowed"
        >
          📎 Photo
        </button>
        <button
          type="button"
          onClick={props.onOpenGifPicker}
          disabled={props.gifDisabled}
          title={
            props.gifDisabled
              ? "Up to 4 attachments per post"
              : "Pick a GIF from Tenor"
          }
          className="rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text2)] hover:border-[var(--color-border2)] hover:text-[var(--color-text)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          GIF
        </button>
        {props.photos.length > 0 ? (
          <span className="text-[11px] text-[var(--color-text3)] ml-1">
            {props.photos.length} / 4
          </span>
        ) : null}
      </div>
      {props.photos.length > 0 ? (
        <ul className="grid grid-cols-4 gap-2">
          {props.photos.map((url, idx) => (
            <li key={idx} className="relative">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={url}
                alt="Attached"
                className="w-full h-20 object-cover rounded border border-[var(--color-border)]"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => props.onRemovePhoto(idx)}
                className="absolute top-1 right-1 rounded-full bg-black/70 hover:bg-black/90 text-white text-xs w-5 h-5 flex items-center justify-center"
                aria-label="Remove attachment"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function DestinationPickerRow(props: {
  destination: PostComposerDestination;
  authorAnonymous: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (next: PostComposerDestination) => void;
  onAnonymousToggle: (v: boolean) => void;
}) {
  const label = useMemo(() => {
    if (props.destination.kind === "topic") {
      return `Topic: ${props.destination.name}`;
    }
    return props.authorAnonymous ? "Anonymous (My profile)" : "My profile";
  }, [props.destination, props.authorAnonymous]);

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={props.onToggle}
        className="w-full flex items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-[var(--color-text)] hover:border-[var(--color-border2)] transition-colors"
        aria-expanded={props.open}
      >
        <span>
          <span className="text-[var(--color-text3)] mr-2">Post to</span>
          {label}
        </span>
        <span className="text-[var(--color-text3)]">
          {props.open ? "▴" : "▾"}
        </span>
      </button>

      {props.open ? (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] divide-y divide-[var(--color-border)]">
          <DestinationOption
            label="My profile"
            description="Public post on your timeline"
            active={
              props.destination.kind === "profile" && !props.authorAnonymous
            }
            onClick={() => {
              props.onAnonymousToggle(false);
              props.onChange({ kind: "profile", anonymous: false });
            }}
          />
          <DestinationOption
            label="Anonymous"
            description="Post on your profile without revealing your handle"
            active={
              props.destination.kind === "profile" && props.authorAnonymous
            }
            onClick={() => {
              props.onAnonymousToggle(true);
              props.onChange({ kind: "profile", anonymous: true });
            }}
          />
          <TopicSearchPicker
            onPick={(t) =>
              props.onChange({ kind: "topic", slug: t.slug, name: t.name })
            }
            currentSlug={
              props.destination.kind === "topic"
                ? props.destination.slug
                : null
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function DestinationOption(props: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`w-full text-left px-3 py-2 transition-colors hover:bg-[var(--color-surface2)] ${
        props.active ? "bg-[var(--color-surface2)]" : ""
      }`}
    >
      <div className="text-sm text-[var(--color-text)] flex items-center gap-2">
        {props.label}
        {props.active ? (
          <span
            className="text-xs"
            style={{ color: "var(--color-brand)" }}
          >
            ✓
          </span>
        ) : null}
      </div>
      <div className="text-xs text-[var(--color-text3)]">
        {props.description}
      </div>
    </button>
  );
}

function TopicSearchPicker(props: {
  onPick: (t: TopicRecord) => void;
  currentSlug: string | null;
}) {
  const [q, setQ] = useState("");
  const [followedOnly, setFollowedOnly] = useState(true);
  const [topics, setTopics] = useState<TopicRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial load: followed topics; debounce on text input.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.searchTopics({
          q,
          followedOnly,
          limit: 10,
        });
        setTopics(res.topics);
      } catch {
        setTopics([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, followedOnly]);

  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-2 mb-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search topics…"
          className="flex-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2 py-1 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text3)] focus:outline-none focus:border-[var(--color-border2)]"
        />
        <label className="text-xs text-[var(--color-text2)] flex items-center gap-1">
          <input
            type="checkbox"
            checked={followedOnly}
            onChange={(e) => setFollowedOnly(e.target.checked)}
          />
          Followed
        </label>
      </div>

      {loading ? (
        <div className="text-xs text-[var(--color-text3)] px-1 py-2">
          Searching…
        </div>
      ) : topics.length === 0 ? (
        <div className="text-xs text-[var(--color-text3)] px-1 py-2">
          {q ? "No topics match." : "Search to find a topic."}
        </div>
      ) : (
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {topics.map((t) => (
            <li key={t.slug}>
              <button
                type="button"
                onClick={() => props.onPick(t)}
                className={`w-full text-left rounded px-2 py-1 transition-colors hover:bg-[var(--color-surface2)] ${
                  props.currentSlug === t.slug
                    ? "bg-[var(--color-surface2)]"
                    : ""
                }`}
              >
                <div className="text-sm text-[var(--color-text)]">
                  {t.name}
                </div>
                <div className="text-xs text-[var(--color-text3)]">
                  {t.followerCount} followers · {t.marketCount} markets
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MarketPositionAttacher(props: {
  topicSlug: string;
  attachedMarketId: string | null;
  onChange: (id: string | null) => void;
}) {
  const [rows, setRows] = useState<TopicMarketWithUserPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    void api
      .listMyPositionsOnTopic(props.topicSlug)
      .then((res) => {
        if (!cancelled) setRows(res.rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load positions.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.topicSlug]);

  const withPositions = rows.filter(
    (r) => r.userYesShares !== "0" || r.userNoShares !== "0",
  );

  if (loading) {
    return (
      <div className="text-xs text-[var(--color-text3)] px-1 py-2">
        Loading your positions on this topic…
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="text-xs px-1 py-2"
        style={{ color: "var(--color-status-negative)" }}
      >
        {error}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="text-xs text-[var(--color-text3)] px-1 py-2">
        No markets on this topic yet.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-xs text-[var(--color-text3)]">
        Attach a position (optional)
      </div>
      <div className="rounded-md border border-[var(--color-border)] divide-y divide-[var(--color-border)] max-h-40 overflow-y-auto">
        <button
          type="button"
          onClick={() => props.onChange(null)}
          className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-[var(--color-surface2)] ${
            props.attachedMarketId === null
              ? "bg-[var(--color-surface2)]"
              : ""
          }`}
        >
          <span className="text-[var(--color-text2)]">No market attached</span>
        </button>
        {(withPositions.length > 0 ? withPositions : rows).map((r) => (
          <button
            key={r.marketId}
            type="button"
            onClick={() => props.onChange(r.marketId)}
            className={`w-full text-left px-3 py-2 transition-colors hover:bg-[var(--color-surface2)] ${
              props.attachedMarketId === r.marketId
                ? "bg-[var(--color-surface2)]"
                : ""
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-[var(--color-text)] truncate">
                {r.title}
              </span>
              {r.sentimentHint === "bullish" ? (
                <span
                  className="text-xs"
                  style={{ color: "var(--color-yes)" }}
                >
                  ↗ Bullish
                </span>
              ) : r.sentimentHint === "bearish" ? (
                <span
                  className="text-xs"
                  style={{ color: "var(--color-no)" }}
                >
                  ↘ Bearish
                </span>
              ) : null}
            </div>
            <div className="text-xs text-[var(--color-text3)]">
              YES {r.userYesShares} · NO {r.userNoShares}
              {r.yesPriceCents !== null
                ? ` · YES ${r.yesPriceCents}¢`
                : ""}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function VisibilitySelector(props: {
  value: PostVisibility;
  onChange: (v: PostVisibility) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-[var(--color-text3)]">Visibility</div>
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value as PostVisibility)}
        className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-border2)]"
      >
        {VISIBILITY_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className="text-xs text-[var(--color-text3)]">
        {VISIBILITY_OPTIONS.find((o) => o.value === props.value)?.helper}
      </div>
    </div>
  );
}
